import { useCallback, useEffect, useRef } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { Home, Plus, LineChart, MessageCircle, Menu } from 'lucide-react';
import { Icon } from '../ui/Icon';
import { useRepeatLastDose } from '../../lib/repeatLastDose';
import { PRELOADERS } from '../../routes';

// Preload a route's chunk on hover/focus — the browser dedupes a repeat
// import call, so the runtime click cost is zero once the chunk is
// cached. Wrapped in a no-throw helper so a missing entry in PRELOADERS
// (Today, Taper — both eager) is a no-op. See routes.tsx PRELOADERS.
function preload(to: string): void {
  PRELOADERS[to]?.().catch(() => {
    // Best-effort. A failed prefetch shouldn't surface anywhere.
  });
}

// 5 slots: Today · Taper · Log (FAB) · Chat · More
// Phase 6 swap — the Chat Buddy persona surface replaces Insights in the
// primary nav. Insights doesn't disappear: a "Your insights" card on
// Today and an Insights row in /app/more link the existing
// /app/insights route (still registered in routes.tsx).
//
// Log is a tap-or-hold button on touch devices: tap opens the dose
// sheet (current behavior), press-and-hold (700ms) posts a duplicate
// of the last dose with no sheet — the zero-friction path for the
// most-common save. Hold is touch-only (mouse fires onClick on
// release, which we want), so cursor users always get the sheet.

const TABS = [
  { to: '/app', icon: Home, label: 'Today' },
  { to: '/app/taper', icon: LineChart, label: 'Taper' },
  { to: '#log', icon: Plus, label: 'Log', special: 'log' as const },
  { to: '/app/buddy', icon: MessageCircle, label: 'Chat' },
  { to: '/app/more', icon: Menu, label: 'More' },
];

const HOLD_MS = 700;
const HOLD_MOVE_TOLERANCE_PX = 8;

export function TabBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { tryRepeat, available } = useRepeatLastDose();

  const timerRef = useRef<number | null>(null);
  const heldRef = useRef(false);
  const movedRef = useRef(false);
  const startXYRef = useRef<{ x: number; y: number } | null>(null);

  useEffect(
    () => () => {
      if (timerRef.current != null) window.clearTimeout(timerRef.current);
    },
    [],
  );

  const openSheet = useCallback(() => {
    navigate({ pathname: location.pathname, hash: '#log' });
  }, [navigate, location.pathname]);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLButtonElement>) => {
      // Only enable hold-to-repeat on touch/pen — mouse users get the
      // standard tap-only behavior so right-clicks and quick clicks
      // don't accidentally trigger a repeat.
      if (e.pointerType === 'mouse') return;
      if (!available) return;
      heldRef.current = false;
      movedRef.current = false;
      startXYRef.current = { x: e.clientX, y: e.clientY };
      timerRef.current = window.setTimeout(() => {
        heldRef.current = true;
        timerRef.current = null;
        void tryRepeat();
      }, HOLD_MS);
    },
    [available, tryRepeat],
  );

  const handlePointerMove = useCallback((e: React.PointerEvent<HTMLButtonElement>) => {
    if (!startXYRef.current) return;
    const dx = e.clientX - startXYRef.current.x;
    const dy = e.clientY - startXYRef.current.y;
    if (Math.hypot(dx, dy) > HOLD_MOVE_TOLERANCE_PX) {
      movedRef.current = true;
      if (timerRef.current != null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    }
  }, []);

  const cancelHold = useCallback(() => {
    if (timerRef.current != null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    startXYRef.current = null;
  }, []);

  const handleClick = useCallback(() => {
    // Suppress the synthetic click that follows a completed hold.
    if (heldRef.current) {
      heldRef.current = false;
      return;
    }
    openSheet();
  }, [openSheet]);

  // Keyboard alternative to the touch press-and-hold gesture (WCAG 2.2
  // SC 2.5.1 Pointer Gestures). Shift+Enter on the focused Log button
  // posts a duplicate of the last dose, mirroring the touch hold. The
  // aria-label below documents this so screen-reader users hear the
  // shortcut, not just sighted touch users.
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>) => {
      if (!available) return;
      if (e.key === 'Enter' && e.shiftKey) {
        e.preventDefault();
        heldRef.current = true; // suppress the subsequent click → sheet
        void tryRepeat();
      }
    },
    [available, tryRepeat],
  );

  return (
    <nav className="tabbar" aria-label="Primary">
      {TABS.map((t) => {
        if (t.special === 'log') {
          return (
            <button
              key={t.label}
              type="button"
              className="tabbar-item"
              onClick={handleClick}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={cancelHold}
              onPointerCancel={cancelHold}
              onPointerLeave={cancelHold}
              onKeyDown={handleKeyDown}
              aria-label={
                available
                  ? 'Log a dose. Press and hold on touch, or Shift+Enter on keyboard, to repeat the last dose.'
                  : 'Log a dose'
              }
            >
              <span className="tabbar-log">
                <Icon icon={t.icon} size={22} strokeWidth={2.25} />
              </span>
              <span>{t.label}</span>
            </button>
          );
        }
        return (
          <NavLink
            key={t.to}
            to={t.to}
            end={t.to === '/app'}
            className={() => `tabbar-item`}
            onPointerEnter={() => preload(t.to)}
            onFocus={() => preload(t.to)}
          >
            {({ isActive }) => (
              <>
                <Icon icon={t.icon} size={22} className="tabbar-icon" aria-current={isActive ? 'page' : undefined} />
                <span>{t.label}</span>
              </>
            )}
          </NavLink>
        );
      })}
    </nav>
  );
}
